/* eslint-disable react-hooks/exhaustive-deps */
import styled from '@emotion/styled';
import { Collapse, Divider, Space } from 'antd';
import { Title } from 'app/components/Typography/Title';
import { InspectionCriteria } from 'app/features/inspection/types/InspectionCriteria';
import { IShippingInformation } from 'app/types/interfaces/Product';
import { get } from 'lodash';
import React from 'react';
import { SectionFields } from '../../constants';
import { InfoLine, Status } from '../Elements';
import SectionCriteria from '../SectionCriteria';
import { getRequiredCompletedFields, parserReportTemplate } from './helper';

const StyledCollapse = styled(Collapse)`
  background: #fff;
  &.ant-collapse-borderless > .ant-collapse-item {
    border-bottom: unset;
    margin-bottom: 20px;
  }
`;
const StyledPanel = styled(Collapse.Panel)`
  &.sectionHeader {
    // border: 1px solid #669eff !important;
    border-radius: 3px;
    .ant-collapse-header {
      background: #fff;
      font-size: 14px;
      font-weight: 500;
    }
    .ant-collapse-content {
      display: block;
    }
    .ant-collapse-content-inactive {
      background: #fff;
      display: unset !important;
      opacity: 1 !important;
      .ant-collapse-content-box {
        max-height: 40px;
        overflow: hidden;
      }
    }
    .ant-collapse-content {
      border-top: 0px;
    }
    .ant-collapse-content-box {
      padding-top: 0px;
      background: #fff;
    }
  }
`;
const StyledHeaderSection = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #232b3a;
`;
type Props = {
  inspectionCriteria: InspectionCriteria;
  poItemId: number;
  isSignSample: number;
  poItem: any;
  attachments: any[];
  shippingInformation: IShippingInformation[];
  onChangeConclusion: (
    conclusion: 'passed' | 'rejected' | 'failed' | 'pending'
  ) => void;
  report: any;
  progress: {
    poItems: any[];
    percentage: string;
    poItemId: string;
    sectionProgress: {
      [key: string]: string;
    };
  };
};
const ExtraInspectionCriteria: React.FC<Props> = ({
  poItemId,
  poItem,
  attachments,
  shippingInformation,
  onChangeConclusion,
  report,
  progress: inspectionProgress
}) => {
  const overallProgress = (inspectionProgress?.poItems || []).find(
    ({ poItemId }: any) => poItemId === poItem.id
  );
  const [, setConclusion] = React.useState('pending');
  const [expanded, setExpanded] = React.useState<string[]>([]);

  const parsedReport: Array<any> = React.useMemo(() => {
    // Because report is JSON String should parser toObject.
    return parserReportTemplate(report) as any[];
  }, [report]);

  const sections = React.useMemo(() => {
    return parsedReport.map((section) => {
      const { value, requiredFields, completedFields } =
        getRequiredCompletedFields(section, attachments as any[]);

      return {
        label: section.value,
        name: section.name,
        fields: section.fields,
        values: value,
        completedFields,
        requiredFields,
        overallProgress: get(
          overallProgress,
          `sectionProgress.${section.name}`
        ),
        sectionResult: get(overallProgress, `sectionResults.${section.name}`),
        progress: isNaN((completedFields.length / requiredFields.length) * 100)
          ? 0
          : (completedFields.length / requiredFields.length) * 100
      };
    });
  }, [parsedReport, poItem, attachments, inspectionProgress]);

  const progress = React.useMemo(() => {
    if (!sections.length) return 0;
    const totalProgress = sections.reduce((acc, current) => {
      return acc + current.progress;
    }, 0);
    return `${(
      totalProgress /
      sections.filter((section) => {
        return !!section.requiredFields.length;
      }).length
    ).toFixed(0)}`;
  }, [sections]);

  React.useEffect(() => {
    const isPending = sections
      .filter((section) => section.name !== 'importantRemarksAttachments')
      .some(
        ({ sectionResult }: any) =>
          sectionResult === 'pending' || !sectionResult
      );
    const isPassed = sections.every(
      ({ sectionResult }: any) => sectionResult === 'passed'
    );
    // const isRejected = sections.every(
    //   ({ sectionResult }: any) => sectionResult === 'rejected'
    // );
    if (isPending) {
      onChangeConclusion('pending');
      setConclusion('pending');
      return;
    }
    if (isPassed) {
      onChangeConclusion('passed');
      setConclusion('passed');
      return;
    }
    onChangeConclusion('failed');
    setConclusion('failed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections]);

  return (
    <Collapse>
      <StyledPanel
        className="sectionHeader"
        forceRender
        header="Inspection Criteria"
        key="inspection-criteria"
      >
        <Space size={70}>
          <InfoLine
            label="Item Conclusion: "
            value={<Status status={overallProgress?.result as any}></Status>}
          ></InfoLine>
          <InfoLine
            label="Progress: "
            value={`${parseInt(
              (overallProgress?.percentage || progress) as string,
              10
            )}%`}
          />
        </Space>
        <Divider></Divider>
        <Title level={5}>Section:</Title>
        <StyledCollapse
          ghost
          bordered={false}
          onChange={(keys) => {
            setExpanded(keys as string[]);
          }}
        >
          {sections.map((section, index) => {
            const Fields = SectionFields[section.name];
            const status = section.sectionResult;
            return (
              <SectionCriteria
                values={{
                  ...section.values,
                  poItem: poItem,
                  shippingInformation
                }}
                numOfRequiredField={section.requiredFields.length}
                numOfCompletedField={section.completedFields.length}
                expanded={expanded.indexOf(section.name) > -1}
                key={section.name}
                section={section}
                sectionLabel={section.label}
                remark={section.values.remark}
                sectionKey={`${poItemId}_${section.name}`}
                fields={Fields}
                title={
                  <Space size={70} align="center">
                    <StyledHeaderSection className="ant-collapse-header-text">
                      {`${index + 1}  ${section.label}`}
                    </StyledHeaderSection>
                    {section.name !== 'importantRemarksAttachments' && (
                      <InfoLine
                        label="Result:"
                        value={<Status status={status as any}></Status>}
                      ></InfoLine>
                    )}
                  </Space>
                }
              ></SectionCriteria>
            );
          })}
        </StyledCollapse>
      </StyledPanel>
    </Collapse>
  );
};

export default ExtraInspectionCriteria;
